import React, { useState } from 'react';
import axios from 'axios';
import { ShieldCheck, ExternalLink, Info } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { formatApiError } from "../../lib/apiError";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { child_name: "", child_lastname: "", child_second_lastname: "", birth_date: "", curp: "", legal_guardian: false, initial_deposit: 0, goal_amount: "", goal_label: "" };
const CURP_LOOKS_OK = (c) => /^[A-Z]{4}\d{6}[HM][A-Z]{5}[A-Z\d]\d$/.test(c);

export const ChildIdentityFields = ({ data, onChange }) => (
  <>
    <div className="rounded-xl bg-brand-lightBlue/60 p-3 text-xs text-brand-dark flex gap-2" data-testid="identity-notice">
      <Info className="w-4 h-4 text-brand-blue shrink-0 mt-0.5" />
      <p>Cada alcancía está ligada a un menor bajo tu custodia. Captura los datos tal como aparecen en su acta de nacimiento y CURP.</p>
    </div>
    <div className="space-y-2"><Label>Nombre(s)</Label><Input required data-testid="child-name-input" placeholder="Ej: Sofía Valentina" value={data.child_name} onChange={e => onChange({ ...data, child_name: e.target.value })} /></div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2"><Label>Apellido paterno</Label><Input required data-testid="child-lastname-input" placeholder="Ej: García" value={data.child_lastname} onChange={e => onChange({ ...data, child_lastname: e.target.value })} /></div>
      <div className="space-y-2"><Label>Apellido materno</Label><Input data-testid="child-second-lastname-input" placeholder="Ej: López" value={data.child_second_lastname} onChange={e => onChange({ ...data, child_second_lastname: e.target.value })} /></div>
    </div>
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2"><Label>Fecha de nacimiento</Label><Input required type="date" data-testid="child-birthdate-input" value={data.birth_date} onChange={e => onChange({ ...data, birth_date: e.target.value })} /></div>
      <div className="space-y-2">
        <Label className="flex items-center justify-between">CURP <a href="https://www.gob.mx/curp/" target="_blank" rel="noreferrer" className="text-[11px] text-brand-blue hover:underline inline-flex items-center gap-1" data-testid="curp-gobmx-link">Consultar <ExternalLink className="w-3 h-3" /></a></Label>
        <Input required maxLength={18} data-testid="child-curp-input" placeholder="18 caracteres" className={`font-mono uppercase tracking-wider ${data.curp.length === 18 ? (CURP_LOOKS_OK(data.curp) ? "border-brand-green" : "border-red-400") : ""}`} value={data.curp} onChange={e => onChange({ ...data, curp: e.target.value.toUpperCase().replace(/\s/g, "") })} />
      </div>
    </div>
  </>
);

export const NewChildForm = ({ onCreated }) => {
  const [data, setData] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${BACKEND_URL}/api/accounts`, {
        ...data,
        child_second_lastname: data.child_second_lastname.trim() || null,
        initial_deposit: parseFloat(data.initial_deposit) || 0,
        goal_amount: data.goal_amount ? parseFloat(data.goal_amount) : null,
        goal_label: data.goal_label.trim() || null,
      });
      toast.success("Alcancía creada y CURP validado");
      setData(EMPTY);
      onCreated();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail, "Revisa los datos del menor"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 pt-2" data-testid="new-account-form">
      <ChildIdentityFields data={data} onChange={setData} />
      <label className="flex items-start gap-2 text-xs text-gray-600 cursor-pointer rounded-xl border border-gray-100 p-3 hover:border-brand-blue/40">
        <input type="checkbox" data-testid="legal-guardian-checkbox" checked={data.legal_guardian} onChange={(e) => setData({ ...data, legal_guardian: e.target.checked })} className="mt-0.5 accent-[#007fbb]" />
        <span><strong>Soy tutor legal</strong> del menor y sus apellidos no coinciden con los míos. Declaro que cuento con la custodia legal.</span>
      </label>
      <div className="space-y-2"><Label>Depósito inicial</Label><Input type="number" min="0" data-testid="new-account-deposit-input" value={data.initial_deposit} onChange={e => setData({ ...data, initial_deposit: e.target.value })} /></div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Meta de ahorro (opcional)</Label><Input type="number" min="0" data-testid="new-account-goal-amount" placeholder="Ej: 250000" value={data.goal_amount} onChange={e => setData({ ...data, goal_amount: e.target.value })} /></div>
        <div className="space-y-2"><Label>Nombre de la meta</Label><Input maxLength={60} data-testid="new-account-goal-label" placeholder="Ej: Universidad" value={data.goal_label} onChange={e => setData({ ...data, goal_label: e.target.value })} /></div>
      </div>
      <Button type="submit" disabled={saving} data-testid="new-account-submit-btn" className="w-full bg-brand-green text-brand-dark font-bold h-11"><ShieldCheck className="w-4 h-4 mr-2" /> {saving ? "Validando CURP..." : "Crear alcancía"}</Button>
    </form>
  );
};

export const IdentityForm = ({ account, onSaved }) => {
  const [data, setData] = useState({ child_name: account.child_name || "", child_lastname: account.child_lastname || "", child_second_lastname: account.child_second_lastname || "", birth_date: account.birth_date || "", curp: "" });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/accounts/${account.id}/identity`, { ...data, child_second_lastname: data.child_second_lastname.trim() || null });
      toast.success("Datos del menor verificados");
      onSaved();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Revisa los datos del menor");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4 pt-2" data-testid="identity-form">
      <ChildIdentityFields data={data} onChange={setData} />
      <Button type="submit" disabled={saving} data-testid="identity-submit-btn" className="w-full bg-brand-blue text-white font-bold h-11"><ShieldCheck className="w-4 h-4 mr-2" /> {saving ? "Validando CURP..." : "Guardar y validar"}</Button>
    </form>
  );
};
